/**
 * Copyright Highway9 Networks Inc.
 *
 * Comlink batching layer for Redux slice patching.
 *
 * When a websocket flush updates radios, edges, and devices in the same tick,
 * eventContext fires multiple `await workerInstance.patch(...)` calls. Without
 * batching, each call is a separate Comlink round-trip (structuredClone × N).
 *
 * This coordinator queues patch requests during the current synchronous turn,
 * then flushes once on queueMicrotask so all listeners from one flush share
 * a single `applyBatch` worker call.
 */
import { WebSocketMessage } from "~/types/WebsocketMessage";
import type { PatchRequest, PatchResult } from "./workers";

export type PatchWorker = {
  applyBatch(requests: PatchRequest[]): Promise<PatchResult[]>;
  patch(original: unknown[], patches: WebSocketMessage[]): Promise<unknown[]>;
};

interface QueuedPatch {
  requestId: string;
  data: unknown;
  patches: WebSocketMessage[];
  resolve: (patched: unknown) => void;
  reject: (error: unknown) => void;
}

let requestCounter = 0;

export class PatchBatchCoordinator {
  private queue: QueuedPatch[] = [];
  private flushScheduled = false;

  constructor(private readonly worker: PatchWorker) {}

  patch<T>(data: T[], patches: WebSocketMessage[]): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      const requestId = `patch-${++requestCounter}`;
      this.queue.push({
        requestId,
        data,
        patches,
        resolve: resolve as (patched: unknown) => void,
        reject,
      });
      // Defer flush so sibling evt.on handlers can enqueue in the same tick.
      this.scheduleFlush();
    });
  }

  private scheduleFlush() {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    queueMicrotask(() => void this.flush());
  }

  private async flush() {
    this.flushScheduled = false;
    const batch = this.queue.splice(0);
    if (batch.length === 0) return;

    try {
      const results =
        batch.length === 1
          // Avoid applyBatch overhead when only one listener patched this tick.
          ? [{
              requestId: batch[0].requestId,
              patched: await this.worker.patch(batch[0].data as never[], batch[0].patches),
            }]
          : await this.worker.applyBatch(
              batch.map(({ requestId, data, patches }) => ({ requestId, data, patches })),
            );

      const resultById = new Map(results.map((result) => [result.requestId, result.patched]));
      for (const item of batch) {
        const patched = resultById.get(item.requestId);
        if (patched === undefined) {
          item.reject(new Error(`Patch batch missing result for ${item.requestId}`));
        } else {
          item.resolve(patched);
        }
      }
    } catch (error) {
      for (const item of batch) {
        item.reject(error);
      }
    }
  }
}
